<template>
  <v-container>
    <v-alert type="error" icon="fas fa-exclamation" elevation="2" dense :value="error != ''" dismissible @click="closeAlert">{{error}}</v-alert>
    <v-card elevation="1" outlined class="filterCard">
      <div style="display: inline-flex;">
        <h2 style="margin-bottom: 10px; margin-left: 10px;">Filters</h2>
      </div>
      <div style="display: inline-flex; float: right; margin-right: 10px;">
        <v-btn color="primary" @click="clearFilters()">Clear Filters</v-btn>
      </div>
      <v-row no-gutters>
        <v-col>
          <v-text-field 
            outlined 
            class="flexiFilter" 
            label="Job Ref." 
            v-model="searchedJobReference"
            @change="onSearchChanged" 
            clearable></v-text-field>
        </v-col>
        <v-col>
          <v-select 
            outlined 
            class="flexiFilter"  
            label="State" 
            :items="states" 
            item-text="StateName" 
            item-value="StateID" 
            v-model="selectedStateID" 
            @change="onSearchChanged" 
            clearable
            :menu-props="{ top: false, offsetY: true }"></v-select>
        </v-col>
        <v-col>
          <v-text-field 
            outlined 
            class="flexiFilter"  
            label="location" 
            v-model="searchedLocation" 
            @change="onSearchChanged" 
            clearable></v-text-field>
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col>
          <v-select 
            outlined 
            class="flexiFilter"  
            multiple 
            chips
            label="Status" 
            :items="statuses" 
            item-text="JobStatusName" 
            item-value="JobStatusID" 
            v-model="selectedStatusIDs" 
            @change="onSearchChanged" 
            clearable
            :menu-props="{ top: false, offsetY: true }">
            <template #selection="{ item }">
              <v-chip :color="item.ColourHex" style="color: white;">{{item.JobStatusName}}</v-chip>
            </template>
          </v-select>
        </v-col>
        <v-col>
          <DateRangePicker 
            ref="jobStartDatePicker"
            class="flexiFilter"  
            @onInput="startDateInputHandler" 
            title="Job Start Date Range"></DateRangePicker>
        </v-col>
        <v-col>
          <DateRangePicker
            ref="jobFinalisedDatePicker"
            class="flexiFilter" 
            @onInput="finalisedDateInputHandler" 
            title="Job Finalised Date Range"></DateRangePicker>
        </v-col>
      </v-row>
    </v-card>
    <JobList ref="jobListChild" @onLoad="errorHandler" :jobReference="searchedJobReference" :stateID="selectedStateID" :location="searchedLocation" :statusIDs="selectedStatusIDs" :startDateFrom="selectedStartDateFrom" :startDateTo="selectedStartDateTo" :finalisedDateFrom="selectedFinalisedDateFrom" :finalisedDateTo="selectedFinalisedDateTo"></JobList>
  </v-container>
</template>

<script lang="ts">
import { Vue, Component, Prop } from 'vue-property-decorator';
import State from '../../../shared/models/state';
import JobStatus from '../../../shared/models/jobStatus';
import Location from '../../../shared/models/location';
import JobList from '@/components/jobs/JobList.vue';
import DateRangePicker from '@/components/global/DateRangePicker.vue';
import * as locationAPI from '@/api/locationAPI';
import * as stateAPI from '@/api/stateAPI';
import * as jobStatusAPI from '@/api/jobStatusAPI';

@Component({
  components: {
    JobList,
    DateRangePicker
  },
})
export default class JobsView extends Vue{
  $refs!: {
    jobListChild: JobList
    jobStartDatePicker: DateRangePicker
    jobFinalisedDatePicker: DateRangePicker
  }

  @Prop(String) public locationID!: string | null;
  @Prop(String) public upcomingStatus!: string | null;

  private error = "";
  private searchedJobReference: string | null = null;
  private selectedStateID: number | null = null;
  private searchedLocation: string |null = null;
  private selectedStatusIDs: number[] | null = null;
  private selectedStartDateFrom: string | null = null;
  private selectedStartDateTo: string | null = null;
  private selectedFinalisedDateFrom: string | null = null;
  private selectedFinalisedDateTo: string | null = null;

  private states: State[] = [];
  private statuses: JobStatus[] = [];

  private async mounted() {
    try {
      if (this.locationID != null && this.locationID != "null") {
        const location: Location = await locationAPI.getLocation(this.locationID);

        this.selectedStateID = location.StateID;
        this.searchedLocation = location.LocationName;
        this.$refs.jobListChild.load();
      }

      this.states = await stateAPI.getStates();
      this.statuses = await jobStatusAPI.getJobStatuses();

     if (this.upcomingStatus != null && this.upcomingStatus == "upcoming") {
        const upcomingStatuses: number[] = this.statuses.filter(x => x.IsUpcoming).map(x => x.JobStatusID);
        this.selectedStatusIDs = upcomingStatuses;
      }
    }
    catch (ex) {
      this.error = (ex as Error).message;
    }
  }

  private async onSearchChanged() {
    this.$refs.jobListChild.load();
  }

  private startDateInputHandler(dates: string[] | null) {
    this.selectedStartDateFrom = dates != null ? dates[0] : null;
    this.selectedStartDateTo = dates != null && dates.length > 1 ? dates[1] : null;

    this.onSearchChanged();
  }

  private finalisedDateInputHandler(dates: string[] | null) {
    this.selectedFinalisedDateFrom = dates != null ? dates[0] : null;
    this.selectedFinalisedDateTo = dates != null && dates.length > 1 ? dates[1] : null;
    
    this.onSearchChanged();
  }

  private clearFilters() {
    this.searchedJobReference = null;
    this.selectedStateID = null;
    this.searchedLocation = null;
    this.selectedStatusIDs = null;
    this.selectedStartDateFrom = null;
    this.selectedStartDateTo = null;
    this.selectedFinalisedDateFrom = null;
    this.selectedFinalisedDateTo = null;

    this.$refs.jobStartDatePicker.clear_NonEmitting();
    this.$refs.jobFinalisedDatePicker.clear_NonEmitting();
    this.$refs.jobListChild.load();
  }
  
  private errorHandler(errorMessasge: string) {
    this.error = errorMessasge;
  }

  private closeAlert() {
    this.error = "";
  }
}
</script>

<style>
.flexiFilter {
  margin-left: 10px !important; 
  margin-right: 10px !important; 
  min-width: 200px;
}
.filterCard {
  padding: 12px !important;
  margin-bottom: 30px;
}
.v-select.v-select--chips:not(.v-text-field--single-line).v-text-field--box .v-select__selections, .v-select.v-select--chips:not(.v-text-field--single-line).v-text-field--enclosed .v-select__selections {
  min-height: 0px !important;
}
</style>